import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import { Card } from "randmarcomps";
import { Info } from "lucide-react";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import { ManufacturerCategory } from "randmar-api-client";
import withManufacturer from "../../contexts/WithManufacturer";
import { Category } from "../../stores/category/Category";
import { Inventory } from "../../stores/product/Product";
import { RootStore } from "../../stores/rootStore";
import EditCategoryInformationIcon from "../categories/EditCategoryInformationIcon";
import CurrencyUtils from "../util/CurrencyUtils";

interface IProps {
  applicationId: string;
  distribution?: Inventory;
  category: Category | ManufacturerCategory;
  store?: RootStore;
}

const intRegEx = new RegExp("^([0-9]+)?$|^$");
const doubleRegEx = new RegExp("^[0-9]+(.[0-9]{0,2})?$|^$");

const isLegacyCategory = (cat: Category | ManufacturerCategory): cat is Category => 'a_gp' in cat;

const MarginsGrid: React.FC<IProps> = ({ distribution, category: categoryProp, store }) => {
  const { userStore } = store!;

  const category = React.useMemo(() => {
    if (isLegacyCategory(categoryProp)) {
      return categoryProp;
    }
    return {
      a_gp: categoryProp.A_GP ?? undefined,
      b_gp: categoryProp.B_GP ?? undefined,
      c_gp: categoryProp.C_GP ?? undefined,
      d_gp: categoryProp.D_GP ?? undefined,
      cost_gp: categoryProp.Cost_GP ?? undefined,
      categoryCode: categoryProp.CategoryCode,
    }
  }, [categoryProp]);

  return (
    <Card className="w-full">
      <div className="flex items-start p-4 mb-4 text-sm border rounded-lg bg-sky-50 border-sky-200 text-sky-800">
        <Info className="flex-shrink-0 w-5 h-5 mr-3 text-sky-500" />
        <div>
          <h3 className="font-bold">Reseller Packages</h3>
          <p className="mt-1">
            {`These represent Randmar's margins ${(distribution && distribution.cost) ? "and part number's unit prices" : ""} for the different reseller tiers.`}
          </p>
        </div>
      </div>
      <div className="w-full overflow-hidden border rounded-lg">
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-center border-r"><CurrencyExchangeIcon /></th>
              <th scope="col" className="px-6 py-3 text-center">Exclusive</th>
              <th scope="col" className="px-6 py-3 text-center">E‑Commerce<br />Academy</th>
              <th scope="col" className="px-6 py-3 text-center">Commercial</th>
              <th scope="col" className="px-6 py-3 text-center">Novexco<br />Basics</th>
              {userStore.isSuperAdmin() && <th scope="col" className="px-6 py-3 text-center">Cost</th>}
            </tr>
          </thead>
          <tbody>
            {distribution && distribution.cost &&
              <tr className="bg-white border-b hover:bg-gray-50">
                <th scope="row" className="px-6 py-4 font-bold text-center text-gray-900 border-r whitespace-nowrap">{distribution.currency}</th>
                <td className="px-6 py-4 font-bold text-center">{typeof category.a_gp === 'number' ? <FormattedNumber value={distribution.cost / (1 - category.a_gp / 100)} {...CurrencyUtils.format} /> : "N/A"}</td>
                <td className="px-6 py-4 font-bold text-center">{typeof category.b_gp === 'number' ? <FormattedNumber value={distribution.cost / (1 - category.b_gp / 100)} {...CurrencyUtils.format} /> : "N/A"}</td>
                <td className="px-6 py-4 font-bold text-center">{typeof category.c_gp === 'number' ? <FormattedNumber value={distribution.cost / (1 - category.c_gp / 100)} {...CurrencyUtils.format} /> : "N/A"}</td>
                <td className="px-6 py-4 font-bold text-center">{typeof category.d_gp === 'number' ? <FormattedNumber value={distribution.cost / (1 - category.d_gp / 100)} {...CurrencyUtils.format} /> : "N/A"}</td>
                {userStore.isSuperAdmin() && <td className="px-6 py-4 font-bold text-center">{typeof category.cost_gp === 'number' ? <FormattedNumber value={distribution.cost / (1 - category.cost_gp / 100)} {...CurrencyUtils.format} /> : "N/A"}</td>}
              </tr>}
            <tr className="bg-gray-50">
              <th scope="row" className="px-6 py-4 font-medium text-center text-gray-900 border-r whitespace-nowrap">Margins</th>
              <td className="px-6 py-4 text-center">
                <div className="flex items-center justify-center space-x-2">
                  <div>{category.a_gp}%</div>
                  {userStore.isSuperAdmin() &&
                    <EditCategoryInformationIcon
                      informationName="A_GP"
                      informationCurrentValue={category.a_gp?.toString()}
                      validateFonction={intRegEx.test.bind(doubleRegEx)}
                      categoryCode={category.categoryCode || ""}
                      isFloatInput={false}
                    />}
                </div>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="flex items-center justify-center space-x-2">
                  <div>{category.b_gp}%</div>
                  {userStore.isSuperAdmin() &&
                    <EditCategoryInformationIcon
                      informationName="B_GP"
                      informationCurrentValue={category.b_gp?.toString()}
                      validateFonction={intRegEx.test.bind(doubleRegEx)}
                      categoryCode={category.categoryCode || ""}
                      isFloatInput={false}
                    />}
                </div>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="flex items-center justify-center space-x-2">
                  <div>{category.c_gp}%</div>
                  {userStore.isSuperAdmin() &&
                    <EditCategoryInformationIcon
                      informationName="C_GP"
                      informationCurrentValue={category.c_gp?.toString()}
                      validateFonction={intRegEx.test.bind(doubleRegEx)}
                      categoryCode={category.categoryCode || ""}
                      isFloatInput={false}
                    />}
                </div>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="flex items-center justify-center space-x-2">
                  <div>{category.d_gp}%</div>
                  {userStore.isSuperAdmin() &&
                    <EditCategoryInformationIcon
                      informationName="D_GP"
                      informationCurrentValue={category.d_gp?.toString()}
                      validateFonction={intRegEx.test.bind(doubleRegEx)}
                      categoryCode={category.categoryCode || ""}
                      isFloatInput={false}
                    />}
                </div>
              </td>
              {userStore.isSuperAdmin() &&
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <div>{category.cost_gp}%</div>
                    {userStore.isSuperAdmin() &&
                      <EditCategoryInformationIcon
                        informationName="Cost_GP"
                        informationCurrentValue={category.cost_gp?.toString()}
                        validateFonction={intRegEx.test.bind(doubleRegEx)}
                        categoryCode={category.categoryCode || ""}
                        isFloatInput={false}
                      />}
                  </div>
                </td>
              }
            </tr>
          </tbody>
        </table>
      </div>
    </Card >
  );
};

export default withManufacturer(inject("store")(observer(MarginsGrid)));
